/* eslint-disable react/jsx-closing-tag-location */
import React, { useState } from 'react';
import InnerImageZoom from 'react-inner-image-zoom';
import { createPortal } from 'react-dom';
import { KatIcon } from '@amzn/katal-react';
import { useMetrics } from 'src/utils';
import { ButtonTypes } from 'src/constants/metrics';

export interface ModalImageCarouselProps {
  startIndex: number;
  images: string[];
  onClose: () => void;
}

export const selectedDotButtonStyle = { fill: '#002F36', stroke: '#FFFFFF' };
export const unSelectedDotButtonStyle = { fill: '#FFFFFF', stroke: '#002F36' };

const ModalImageCarousel: React.FC<ModalImageCarouselProps> = ({ startIndex, images, onClose }) => {
  const validatedStartIndex = startIndex >= 0 && startIndex < images.length ? startIndex : 0;
  const [page, setPage] = useState(validatedStartIndex);
  const { clickMetric } = useMetrics();
  const navigationButtonClassname = 'cursor-pointer border border-solid border-kat-nordic bg-kat-snow p-0 active:bg-kat-seattle h-[48px] w-[28px] ';
  const leftNavButtonClassname = page === 0 ? `${navigationButtonClassname} hidden` : navigationButtonClassname;
  const rightNavButtonClassname = page === images.length - 1 ? `${navigationButtonClassname} hidden` : navigationButtonClassname;
  if (images.length === 0) {
    return null;
  }

  const getDotButtonGroup = () => {
    if (images.length < 2) return null;

    return (
      <div data-testid="carousel-dot-group" className="my-5 flex flex-row gap-3">
        {
            images.map((image, index) => (
              <button
                data-testid="dot-group-button"
                // eslint-disable-next-line react/no-array-index-key
                key={`${image}-${index}`}
                type="button"
                className="cursor-pointer border-0 bg-transparent p-0"
                onClick={() => setPage(index)}
              >
                <svg width="15px" height="15px" xmlns="http://www.w3.org/2000/svg" version="1.1">
                  <circle
                    data-testid="dot-group-circle"
                    cx="7"
                    cy="7"
                    r="6"
                    {
                      ...(page === index ? selectedDotButtonStyle : unSelectedDotButtonStyle)
                    }
                  />
                </svg>
              </button>
            ))
        }
      </div>
    );
  };

  return createPortal(<div
    data-testid="modal-image-carousel"
    className="fixed left-0 top-0 z-[2850] h-full w-full overflow-auto bg-[#333333]/[0.9]"
  >
    <span className="absolute top-[15px] right-[35px] z-20">
      <button
        type="button"
        data-testid="carousel-close"
        className="cursor-pointer border-0 bg-transparent p-0"
        onClick={() => {
          clickMetric({
            metricName: ButtonTypes.imageCarouselClose,
          });
          onClose();
        }}
      >
        <KatIcon className="text-kat-white" name="close" size="medium" />
      </button>
    </span>

    <div className="absolute inset-y-0 left-0 z-10 flex items-center  overflow-hidden">
      <button
        data-testid="carousel-prev-button"
        type="button"
        className={leftNavButtonClassname}
        onClick={() => {
          if (page !== 0) {
            clickMetric({
              metricName: ButtonTypes.imageCarouselPrevious,
            });
            setPage((prevState) => prevState - 1);
          }
        }}
      >
        <KatIcon className="ml-[-6px] mt-[3px]" name="chevron-left" />
      </button>
    </div>

    <div
      data-testid="carousel-image"
      className="relative bottom-0 m-auto my-[50px] flex h-[80vh] w-full max-w-fit items-center"
    >
      <InnerImageZoom hideHint zoomScale={1.5} src={images[page]} />
    </div>
    <div className="relative">
      <div className="absolute bottom-0 flex w-full justify-center">
        {getDotButtonGroup()}
      </div>
    </div>
    <div className="absolute inset-y-0 right-0 z-10 flex items-center overflow-hidden">
      <button
        type="button"
        data-testid="carousel-next-button"
        className={rightNavButtonClassname}
        onClick={() => {
          if (page !== images.length - 1) {
            clickMetric({
              metricName: ButtonTypes.imageCarouselNext,
            });
            setPage((prevState) => prevState + 1);
          }
        }}
      >
        <KatIcon className="ml-[-6px] mt-[3px]" name="chevron-right" />
      </button>
    </div>
  </div>, document.querySelector('#seller-forums') as Element);
};
export default ModalImageCarousel;
